import { useState } from 'react'
import { formatWhen } from '../../lib/schedule'
import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { usersQuery } from '../../lib/users'
import { usePresence, stateLabel } from '../../lib/presence'
import { Avatar } from '../../components/avatar'
import { PresenceDot } from '../../components/presence-dot'
import { Input } from '../../components/ui/input'
import { Button } from '../../components/ui/button'
import { useOpenDM } from '../../components/dm-dialog'

// DIS-56: People directory — everyone active, with presence and status.
// The "Message" shortcut opens (or creates) the DM (DIS-57).

export const Route = createFileRoute('/_app/people')({
  component: PeoplePage,
})

function PeoplePage() {
  const users = useQuery({ ...usersQuery, staleTime: 0 })
  const [q, setQ] = useState('')
  const list = (users.data ?? []).filter((u) => {
    const s = q.trim().toLowerCase()
    return !s || u.handle.includes(s) || u.display_name.toLowerCase().includes(s)
  })
  const presence = usePresence(list.map((u) => u.id))
  const openDM = useOpenDM()
  const { me } = Route.useRouteContext()
  return (
    <>
      <header className="flex h-12 items-center gap-3 border-b border-border px-4" data-testid="channel-header">
        <h1 className="font-semibold">People</h1>
        <span className="text-sm text-fg-muted">{users.data ? `${users.data.length} in the workspace` : ''}</span>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search people" aria-label="Search people" className="ml-auto h-8 w-64" />
      </header>
      <ul className="grid flex-1 auto-rows-min grid-cols-1 gap-2 overflow-y-auto p-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="people-list">
        {list.map((u) => {
          const p = presence[u.id]
          return (
            <li key={u.id} className="flex items-center gap-3 rounded-lg border border-border p-3" data-handle={u.handle}>
              <span className="relative">
                <Avatar user={u} size={40} />
                <PresenceDot state={p?.state} className="absolute -bottom-0.5 -right-0.5" size={12} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">
                  {u.display_name} <span className="font-normal text-fg-muted">@{u.handle}</span>
                  {u.role !== 'member' && <span className="ml-1 rounded bg-border px-1 text-[10px] uppercase">{u.role}</span>}
                </div>
                <div className="truncate text-xs text-fg-muted">
                  {p ? stateLabel[p.state] : '…'}
                  {(p?.status_emoji || p?.status_text) && <span title={p.status_clears_at ? `Until ${formatWhen(p.status_clears_at)}` : undefined}>{` · ${p.status_emoji ?? ''} ${p.status_text ?? ''}`.trimEnd()}</span>}
                </div>
              </div>
              <Button size="sm" variant="secondary" disabled={openDM.isPending || me.role === 'guest'} onClick={() => openDM.mutate(u.id === me.id ? [] : [u.id])} aria-label={`Message ${u.display_name}`}>
                Message
              </Button>
            </li>
          )
        })}
      </ul>
    </>
  )
}
